import { GROCERY_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosGroceryInstance from './AxiosGroceryInstance';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { createInventoryPurchase, formatPackQuantityLabel } from './inventoryApi';
import defaultGroceryImage from './assets/defaultGrocery.jpg';

export { defaultGroceryImage };

function authHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function getGroceryImageUrl(row) {
  const directUrl = row?.imageUrl ?? row?.Image_Url ?? '';
  if (directUrl && String(directUrl).trim()) {
    return String(directUrl).trim().replace(/%22|"$/g, '');
  }
  const path = row?.imagePath ?? row?.Image_Path ?? '';
  if (!path || !String(path).trim()) return '';
  const cleanPath = String(path).trim().replace(/^\/+/, '').replace(/%22|"$/g, '');
  const base = axiosGroceryInstance.defaults.baseURL || process.env.REACT_APP_API_COMPANY_URL || '';
  if (!base) return `/uploads/${cleanPath}`;
  try {
    return `${new URL(base).origin}/uploads/${cleanPath}`;
  } catch {
    return `${base.replace(/\/$/, '')}/uploads/${cleanPath}`;
  }
}

export function getStoreGroceryImageUrl(row) {
  return getGroceryImageUrl(row) || defaultGroceryImage;
}

export function formatGroceryRupee(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return null;
  const text = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
  return `₹${text}`;
}

export function getGroceryItemPriceParts(item) {
  const sellingRaw = item?.sellingPrice;
  if (sellingRaw == null || sellingRaw === '') return null;

  const sellingNum = Number(sellingRaw);
  if (!Number.isFinite(sellingNum)) return null;

  const mrpRaw = item?.mrp;
  const mrpNum =
    mrpRaw != null && mrpRaw !== '' && Number.isFinite(Number(mrpRaw))
      ? Number(mrpRaw)
      : null;

  const unit = String(item?.unitSymbol || item?.unitName || '').trim();
  const packQty = item?.packQuantity;

  let quantityLabel = '';
  if (packQty != null && packQty !== '') {
    quantityLabel = formatPackQuantityLabel(packQty, unit, { spaced: true });
  }

  return {
    sellingPrice: formatGroceryRupee(sellingNum),
    mrp: mrpNum != null ? formatGroceryRupee(mrpNum) : null,
    showMrp: mrpNum != null && mrpNum > sellingNum,
    quantityLabel,
    unit,
  };
}

export function normalizeSupermarket(row) {
  if (!row || typeof row !== 'object') return null;
  const id = row.supermarketId ?? row.Supermarket_Id ?? row.storeId ?? row.Store_Id;
  if (id == null) return null;
  const distanceRaw = row.distanceKm ?? row.distance_km;
  const distanceKm =
    distanceRaw != null && distanceRaw !== '' && Number.isFinite(Number(distanceRaw))
      ? Number(distanceRaw)
      : null;
  return {
    supermarketId: id,
    supermarketName:
      row.supermarketName ?? row.Supermarket_Name ?? row.storeName ?? row.Store_Name ?? '',
    shortCode: row.shortCode ?? row.Short_Code ?? '',
    contactNo: row.contactNo ?? row.Contact_No ?? '',
    email: row.email ?? row.Email ?? '',
    status: row.status ?? row.Status ?? '',
    isDefault: row.isDefault ?? row.Is_Default ?? false,
    distanceKm,
    city: row.city ?? row.address?.city ?? null,
    homeDeliveryAvailable:
      row.homeDeliveryAvailable ?? row.Home_Delivery_Available ?? false,
    imageUrl: row.imageUrl ?? row.Image_Url ?? row.image_url ?? '',
    image: row.image ?? row.Image ?? row.imagePath ?? row.Image_Path ?? '',
  };
}

export function normalizeGroceryCategory(row) {
  if (!row || typeof row !== 'object') return null;
  const id = row.groceryCategoryId ?? row.Grocery_Category_Id;
  if (id == null) return null;
  return {
    groceryCategoryId: id,
    categoryName: row.categoryName ?? row.Category_Name ?? '',
    description: row.description ?? row.Description ?? '',
  };
}

export function normalizeSupermarketGroceryItem(row, supermarket = null) {
  if (!row || typeof row !== 'object') return null;
  const id =
    row.groceryItemId ??
    row.storeInventoryItemId ??
    row.Store_Inventory_Item_Id ??
    row.Grocery_Item_Id;
  if (id == null) return null;
  const supermarketId =
    row.supermarketId ??
    row.Supermarket_Id ??
    row.storeId ??
    row.Store_Id ??
    supermarket?.supermarketId ??
    null;
  const stockRaw =
    row.stockQuantity ??
    row.quantityInStock ??
    row.Stock_Quantity ??
    row.Quantity_In_Stock ??
    null;
  return {
    groceryItemId: id,
    storeInventoryItemId: id,
    inventoryItemId: id,
    supermarketId,
    supermarketName:
      row.supermarketName ??
      row.Supermarket_Name ??
      supermarket?.supermarketName ??
      '',
    groceryCategoryId: row.groceryCategoryId ?? row.Grocery_Category_Id ?? null,
    groceryCategoryName: row.groceryCategoryName ?? row.Grocery_Category_Name ?? '',
    groceryId: row.groceryId ?? row.Grocery_Id ?? row.groceryProductId ?? null,
    groceryProductId: row.groceryProductId ?? row.groceryId ?? row.Grocery_Id ?? null,
    brandId: row.brandId ?? row.Brand_Id ?? null,
    brandName: row.brandName ?? row.Brand_Name ?? '',
    itemName: row.itemName ?? row.Item_Name ?? '',
    barcode: row.barcode ?? row.Barcode ?? '',
    description: row.description ?? row.Description ?? '',
    packQuantity: row.packQuantity ?? row.Pack_Quantity ?? null,
    mrp: row.mrp ?? row.MRP ?? null,
    sellingPrice: row.sellingPrice ?? row.Selling_Price ?? row.unitPrice ?? row.Unit_Price ?? null,
    discountPercent: row.discountPercent ?? row.Discount_Percent ?? null,
    unitId: row.unitId ?? row.Unit_Id ?? null,
    unitName: row.unitName ?? row.Unit_Name ?? '',
    unitSymbol: row.unitSymbol ?? row.Unit_Symbol ?? row.unitName ?? '',
    stockQuantity: stockRaw,
    quantityInStock: stockRaw,
    status: row.status ?? row.Status ?? '',
    notes: row.notes ?? row.Notes ?? '',
    imagePath: row.imagePath ?? row.Image_Path ?? '',
    imageUrl: row.imageUrl ?? row.Image_Url ?? '',
  };
}

/** Exact barcode match (case-insensitive). Used by POS scan-to-cart. */
export function findGroceryItemByBarcode(items, code) {
  const needle = String(code || '').trim().toLowerCase();
  if (!needle) return null;
  return (
    (Array.isArray(items) ? items : []).find(
      (item) => String(item?.barcode || '').trim().toLowerCase() === needle
    ) || null
  );
}

/** Heuristic: scanner-style value (no spaces, compact code). */
export function looksLikeScannedBarcode(value) {
  const text = String(value || '').trim();
  if (text.length < 6 || text.length > 100) return false;
  if (/\s/.test(text)) return false;
  return /^[A-Za-z0-9\-_.]+$/.test(text);
}

function getLoggedInUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.userId ?? user?.id ?? user?.User_Id ?? null;
  } catch {
    return null;
  }
}

/** Active stores (supermarkets) assigned to the logged-in user. */
export async function fetchActiveSupermarkets(userId = null) {
  const effectiveUserId = userId ?? getLoggedInUserId();
  if (!effectiveUserId) {
    return [];
  }

  try {
    const response = await axiosGroceryInstance.get(
      GROCERY_API_PATHS.storesByUserId(effectiveUserId),
      { headers: authHeaders() }
    );
    const raw = response.data?.data ?? response.data?.stores ?? [];
    return (Array.isArray(raw) ? raw : []).map(normalizeSupermarket).filter(Boolean);
  } catch (err) {
    if (err?.response?.status === 404) {
      return [];
    }
    throw err;
  }
}

export async function fetchGroceryCategories() {
  try {
    const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.categories, {
      headers: authHeaders(),
    });
    const raw = Array.isArray(response.data)
      ? response.data
      : response.data?.categories ?? response.data?.data ?? [];
    return (Array.isArray(raw) ? raw : []).map(normalizeGroceryCategory).filter(Boolean);
  } catch (err) {
    if (err?.response?.status === 404) {
      return [];
    }
    throw err;
  }
}

export async function fetchSupermarketGroceryItems(supermarketId, supermarket = null) {
  const response = await axiosGroceryInstance.get(
    GROCERY_API_PATHS.supermarketItems(supermarketId),
    { headers: authHeaders() }
  );
  const raw = response.data?.items ?? (Array.isArray(response.data) ? response.data : []);
  return (Array.isArray(raw) ? raw : [])
    .map((row) => normalizeSupermarketGroceryItem(row, supermarket))
    .filter(Boolean);
}

function appendGroceryItemFields(formData, payload) {
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined) return;
    // Allow clearing optional FK fields (e.g. brandId) on multipart updates.
    if (value == null || value === '') {
      if (key === 'brandId') {
        formData.append(key, '');
      }
      return;
    }
    formData.append(key, String(value));
  });
}

/** POST /groceries/items — creates store_inventory_item_t + stock row. */
export async function createStoreCatalogItem(payload, imageFile = null) {
  if (imageFile) {
    const formData = new FormData();
    appendGroceryItemFields(formData, payload);
    formData.append('image', imageFile, imageFile.name ?? 'item.jpg');
    const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.groceryItems, formData, {
      headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return response.data ?? {};
  }
  const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.groceryItems, payload, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return response.data ?? {};
}

/** PUT /groceries/items/:id */
export async function updateStoreCatalogItem(itemId, payload, imageFile = null) {
  if (imageFile) {
    const formData = new FormData();
    appendGroceryItemFields(formData, payload);
    formData.append('image', imageFile, imageFile.name ?? 'item.jpg');
    const response = await axiosGroceryInstance.put(
      GROCERY_API_PATHS.groceryItemById(itemId),
      formData,
      { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
    );
    return response.data ?? {};
  }
  const response = await axiosGroceryInstance.put(
    GROCERY_API_PATHS.groceryItemById(itemId),
    payload,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

/** DELETE /groceries/items/:id */
export async function deleteStoreCatalogItem(itemId) {
  const response = await axiosGroceryInstance.delete(GROCERY_API_PATHS.groceryItemById(itemId), {
    headers: authHeaders(),
  });
  return response.data ?? {};
}

/** GET /restaurant/:restaurantId/nearby-grocery-stores — stores within radiusKm (default 5). */
export async function fetchNearbyGroceryStores(
  restaurantId,
  { radiusKm = 5, status = 'ACTIVE' } = {}
) {
  const rid = Number(restaurantId);
  if (!rid || Number.isNaN(rid)) {
    return {
      restaurantId: null,
      restaurantName: '',
      radiusKm,
      origin: null,
      count: 0,
      stores: [],
    };
  }

  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantNearbyGroceryStores(rid),
    {
      headers: authHeaders(),
      params: { radiusKm, status },
    }
  );
  const data = response.data ?? {};
  const raw = data.stores ?? [];
  const stores = (Array.isArray(raw) ? raw : []).map(normalizeSupermarket).filter(Boolean);
  return {
    restaurantId: data.restaurantId ?? rid,
    restaurantName: data.restaurantName ?? '',
    radiusKm: data.radiusKm ?? radiusKm,
    origin: data.origin ?? null,
    count: data.count ?? stores.length,
    stores,
  };
}

/** GET /groceries/store/:storeId/nearby-grocery-stores — other stores within radiusKm (default 5). */
export async function fetchNearbyGroceryStoresForStore(
  storeId,
  { radiusKm = 5, status = 'ACTIVE' } = {}
) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) {
    return {
      storeId: null,
      storeName: '',
      radiusKm,
      origin: null,
      count: 0,
      stores: [],
    };
  }

  const response = await axiosRestaurantInstance.get(
    GROCERY_API_PATHS.storeNearbyGroceryStores(sid),
    {
      headers: authHeaders(),
      params: { radiusKm, status },
    }
  );
  const data = response.data ?? {};
  const raw = data.stores ?? [];
  const stores = (Array.isArray(raw) ? raw : []).map(normalizeSupermarket).filter(Boolean);
  return {
    storeId: data.storeId ?? sid,
    storeName: data.storeName ?? '',
    radiusKm: data.radiusKm ?? radiusKm,
    origin: data.origin ?? null,
    count: data.count ?? stores.length,
    stores,
  };
}

function enrichGroceryItemsWithCategories(items, categories) {
  const categoryById = Object.fromEntries(
    categories.map((c) => [String(c.groceryCategoryId), c.categoryName])
  );
  return (Array.isArray(items) ? items : [])
    .filter((row) => {
      const status = String(row.status || '').toUpperCase();
      const stock = Number(row.stockQuantity);
      return status !== 'INACTIVE' && (!Number.isFinite(stock) || stock > 0);
    })
    .map((row) => ({
      ...row,
      groceryCategoryName:
        row.groceryCategoryName ||
        categoryById[String(row.groceryCategoryId)] ||
        'General',
    }));
}

/** Categories + in-stock items for a single store. */
export async function fetchGroceryCatalogForStore(storeId, store = null) {
  const categories = await fetchGroceryCategories();
  let rows = [];
  try {
    rows = await fetchSupermarketGroceryItems(storeId, store);
  } catch {
    rows = [];
  }
  const items = enrichGroceryItemsWithCategories(rows, categories);
  return { categories, items };
}

export async function fetchBuyGroceryCatalog({ restaurantId = null, radiusKm = 5 } = {}) {
  const rid = Number(restaurantId);
  let supermarkets = [];
  let catalogScope = 'user';
  let nearbyMeta = null;

  if (rid && !Number.isNaN(rid)) {
    const nearby = await fetchNearbyGroceryStores(rid, { radiusKm });
    supermarkets = nearby.stores;
    catalogScope = 'nearby';
    nearbyMeta = {
      restaurantId: nearby.restaurantId,
      restaurantName: nearby.restaurantName,
      radiusKm: nearby.radiusKm,
      count: nearby.count,
      origin: nearby.origin,
    };
  } else {
    try {
      supermarkets = await fetchActiveSupermarkets();
    } catch {
      supermarkets = [];
    }
  }

  const categories = await fetchGroceryCategories();

  const itemGroups = await Promise.all(
    supermarkets.map(async (sm) => {
      try {
        return await fetchSupermarketGroceryItems(sm.supermarketId, sm);
      } catch {
        return [];
      }
    })
  );

  const items = enrichGroceryItemsWithCategories(itemGroups.flat(), categories);

  return { categories, items, supermarkets, catalogScope, nearbyMeta };
}

export async function createGroceryOrder(body) {
  const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.groceryOrders, body, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return response.data ?? {};
}

/**
 * POST /restaurant/:restaurantId/groceries/orders
 * Unified restaurant grocery checkout (single or multi-store), like customer API.
 */
export async function createRestaurantGroceryOrder(restaurantId, body) {
  const rid = Number(restaurantId);
  if (!rid || Number.isNaN(rid)) {
    throw new Error('restaurantId is required');
  }
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantGroceryOrders(rid),
    { ...body, restaurantId: rid },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

export async function createRestaurantGroceryRazorpayOrder(restaurantId, body) {
  const rid = Number(restaurantId);
  if (!rid || Number.isNaN(rid)) {
    throw new Error('restaurantId is required');
  }
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantGroceryRazorpayOrder(rid),
    body,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

export async function recordRestaurantGroceryPaymentSuccess(restaurantId, body) {
  const rid = Number(restaurantId);
  if (!rid || Number.isNaN(rid)) {
    throw new Error('restaurantId is required');
  }
  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantGroceryPaymentSuccess(rid),
    body,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

/**
 * After a restaurant grocery order succeeds, create one inventory purchase per store
 * via POST /restaurant/inventory-purchases (supplier = store name).
 */
export async function createPurchasesFromGroceryCheckout({
  restaurantId,
  createdById,
  cartByStore,
  orderData,
  paymentType,
}) {
  const rid = Number(restaurantId);
  const uid = Number(createdById);
  if (!rid || Number.isNaN(rid)) {
    throw new Error('restaurantId is required');
  }
  if (!uid || Number.isNaN(uid)) {
    throw new Error('createdById is required');
  }
  if (!Array.isArray(cartByStore) || cartByStore.length < 1) {
    throw new Error('cartByStore is required');
  }

  const isMultiStore = Boolean(orderData?.masterOrderId);
  const storeOrders = Array.isArray(orderData?.storeOrders) ? orderData.storeOrders : [];
  const purchases = [];

  for (const group of cartByStore) {
    const groceryOrderId = isMultiStore
      ? storeOrders.find((row) => Number(row.storeId) === Number(group.storeId))?.groceryOrderId
      : orderData?.groceryOrderId;

    const items = (group.lines || [])
      .map((line) => {
        const productId = Number(line.groceryId ?? line.groceryProductId);
        if (!productId || Number.isNaN(productId)) return null;
        return {
          productId,
          quantity: line.quantity,
          pricePerUnit: line.unitPrice,
        };
      })
      .filter(Boolean);

    if (!items.length) {
      throw new Error(
        `Cannot create purchase for ${group.storeName || 'store'}: items are not linked to grocery products.`
      );
    }

    const paymentTypeValue = String(paymentType || '').trim();
    const data = await createInventoryPurchase({
      restaurantId: rid,
      supplierName: group.storeName || `Store ${group.storeId}`,
      createdById: uid,
      purchaseStatus: 'NEW',
      ...(groceryOrderId != null ? { groceryOrderId: Number(groceryOrderId) } : {}),
      ...(paymentTypeValue ? { paymentType: paymentTypeValue } : {}),
      items,
    });
    purchases.push(data?.purchase ?? data);
  }

  return purchases;
}

export async function createStoreGroceryOrder(storeId, body) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) {
    throw new Error('storeId is required');
  }
  const response = await axiosRestaurantInstance.post(
    GROCERY_API_PATHS.storeGroceryOrders(sid),
    { ...body, buyerStoreId: sid },
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

export async function createStoreGroceryRazorpayOrder(storeId, body) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) {
    throw new Error('storeId is required');
  }
  const response = await axiosRestaurantInstance.post(
    GROCERY_API_PATHS.storeGroceryRazorpayOrder(sid),
    body,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

export async function recordStoreGroceryPaymentSuccess(storeId, body) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) {
    throw new Error('storeId is required');
  }
  const response = await axiosRestaurantInstance.post(
    GROCERY_API_PATHS.storeGroceryPaymentSuccess(sid),
    body,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

/**
 * After a store grocery order succeeds, create one inventory purchase per seller store.
 */
export async function createPurchasesFromGroceryCheckoutForStore({
  storeId,
  createdById,
  cartByStore,
  orderData,
  paymentType,
}) {
  const sid = Number(storeId);
  const uid = Number(createdById);
  if (!sid || Number.isNaN(sid)) {
    throw new Error('storeId is required');
  }
  if (!uid || Number.isNaN(uid)) {
    throw new Error('createdById is required');
  }
  if (!Array.isArray(cartByStore) || cartByStore.length < 1) {
    throw new Error('cartByStore is required');
  }

  const isMultiStore = Boolean(orderData?.masterOrderId);
  const storeOrders = Array.isArray(orderData?.storeOrders) ? orderData.storeOrders : [];
  const purchases = [];
  for (const group of cartByStore) {
    const groceryOrderId = isMultiStore
      ? storeOrders.find((row) => Number(row.storeId) === Number(group.storeId))?.groceryOrderId
      : orderData?.groceryOrderId;

    const items = (group.lines || [])
      .map((line) => {
        const productId = Number(line.groceryId ?? line.groceryProductId);
        if (!productId || Number.isNaN(productId)) return null;
        return {
          productId,
          quantity: line.quantity,
          pricePerUnit: line.unitPrice,
        };
      })
      .filter(Boolean);

    if (!items.length) {
      throw new Error(
        `Cannot create purchase for ${group.storeName || 'store'}: items are not linked to grocery products.`
      );
    }

    const paymentTypeValue = String(paymentType || '').trim();
    const data = await createInventoryPurchase({
      storeId: sid,
      supplierName: group.storeName || `Store ${group.storeId}`,
      createdById: uid,
      purchaseStatus: 'NEW',
      ...(groceryOrderId != null ? { groceryOrderId: Number(groceryOrderId) } : {}),
      ...(paymentTypeValue ? { paymentType: paymentTypeValue } : {}),
      items,
    });
    purchases.push(data?.purchase ?? data);
  }

  return purchases;
}

export async function fetchRestaurantGroceryOrders(restaurantId) {
  const rid = Number(restaurantId);
  if (!rid || Number.isNaN(rid)) return { orders: [], count: 0 };
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantGroceryOrders(rid),
    { headers: authHeaders() }
  );
  const data = response.data ?? {};
  const raw = data.orders ?? [];
  return {
    restaurantId: data.restaurantId ?? rid,
    count: data.count ?? raw.length,
    orders: (Array.isArray(raw) ? raw : []).map(normalizeGroceryOrder).filter(Boolean),
  };
}

export async function fetchRestaurantGroceryOrderDetail(restaurantId, groceryOrderId) {
  const rid = Number(restaurantId);
  const oid = Number(groceryOrderId);
  if (!rid || Number.isNaN(rid) || !oid || Number.isNaN(oid)) return null;
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantGroceryOrderById(rid, oid),
    { headers: authHeaders() }
  );
  return normalizeGroceryOrder(response.data) ?? response.data ?? null;
}

export async function fetchRestaurantGroceryMasterOrderDetail(restaurantId, masterOrderId) {
  const rid = Number(restaurantId);
  const mid = Number(masterOrderId);
  if (!rid || Number.isNaN(rid) || !mid || Number.isNaN(mid)) return null;
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantGroceryMasterOrderById(rid, mid),
    { headers: authHeaders() }
  );
  return normalizeGroceryMasterOrder(response.data) ?? response.data ?? null;
}

/** Multi-store customer checkout — one master order, child store_order_t per store. */
export async function createGroceryMasterOrder(body) {
  const response = await axiosGroceryInstance.post(GROCERY_API_PATHS.groceryMasterOrders, body, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return response.data ?? {};
}

export async function fetchGroceryMasterOrderDetail(masterOrderId) {
  const id = Number(masterOrderId);
  if (!id || Number.isNaN(id)) return null;
  const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.groceryMasterOrderById(id), {
    headers: authHeaders(),
  });
  return response.data ?? null;
}

/** Normalize store_master_order_t row with nested storeOrders. */
export function normalizeGroceryMasterOrder(row) {
  if (!row || typeof row !== 'object') return null;
  const id = row.masterOrderId ?? row.Master_Order_Id;
  if (id == null) return null;
  const rawChildren = row.storeOrders ?? row.StoreOrders ?? [];
  const storeOrders = (Array.isArray(rawChildren) ? rawChildren : [])
    .map(normalizeGroceryOrder)
    .filter(Boolean);
  return {
    masterOrderId: id,
    customerId: row.customerId ?? row.Customer_Id ?? null,
    orderSource: row.orderSource ?? row.Order_Source ?? '',
    orderType: row.orderType ?? row.Order_Type ?? '',
    orderStatusCode: row.orderStatusCode ?? row.Order_Status_Code ?? '',
    orderStatus: row.orderStatus ?? row.orderStatusCode ?? '',
    grandTotal: row.grandTotal ?? row.Grand_Total ?? 0,
    subTotal: row.subTotal ?? row.Sub_Total ?? 0,
    gstTotal: row.gst ?? row.GST ?? row.gstTotal ?? row.GST_Total ?? 0,
    gst: row.gst ?? row.GST ?? row.gstTotal ?? row.GST_Total ?? 0,
    platformFee: row.platformFee ?? row.Platform_Fee ?? 0,
    deliveryFee: row.deliveryFee ?? row.Delivery_Fee ?? row.deliveryCharge ?? 0,
    convienceFee: row.convienceFee ?? row.Convience_Fee ?? 0,
    createdDate: row.createdDate ?? row.Created_Date ?? '',
    storeOrders,
  };
}

/** Normalize store_order_t row for Orders UI (aligned with restaurant order shape where useful). */
export function normalizeGroceryOrder(row) {
  if (!row || typeof row !== 'object') return null;
  const id = row.groceryOrderId ?? row.Grocery_Order_Id;
  if (id == null) return null;
  const masterOrderId = row.masterOrderId ?? row.Master_Order_Id ?? null;
  const storeOrderSequence = row.storeOrderSequence ?? row.Store_Order_Sequence ?? null;
  const displayOrderRef =
    row.displayOrderRef ??
    (masterOrderId != null && storeOrderSequence != null
      ? `${masterOrderId}-${storeOrderSequence}`
      : String(id));
  const rawLines = row.lineItems ?? row.LineItems ?? [];
  const lineItems = (Array.isArray(rawLines) ? rawLines : []).map((li) => ({
    lineItemId: li.lineItemId ?? li.LineItem_Id,
    groceryItemId: li.groceryItemId ?? li.Grocery_Item_Id,
    itemName: li.itemName ?? li.Item_Name ?? '',
    quantity: li.quantity ?? li.Quantity ?? 0,
    unitPrice: li.unitPrice ?? li.Unit_Price ?? 0,
    totalPrice: li.totalPrice ?? li.Total_Price ?? 0,
  }));
  return {
    ...row,
    orderId: id,
    groceryOrderId: id,
    masterOrderId,
    storeOrderSequence,
    displayOrderRef,
    storeId: row.storeId ?? row.Store_Id ?? null,
    storeName: row.storeName ?? row.Store_Name ?? '',
    orderSource: row.orderSource ?? row.Order_Source ?? '',
    orderType: row.orderType ?? row.Order_Type ?? '',
    orderStatusCode: row.orderStatusCode ?? row.Order_Status_Code ?? '',
    orderStatus: row.orderStatus ?? row.orderStatusCode ?? row.Order_Status_Code ?? '',
    totalPrice: row.grandTotal ?? row.Grand_Total ?? row.subTotal ?? 0,
    grandTotal: row.grandTotal ?? row.Grand_Total ?? 0,
    subTotal: row.subTotal ?? row.Sub_Total ?? 0,
    gstTotal: row.gst ?? row.GST ?? row.gstTotal ?? row.GST_Total ?? 0,
    gst: row.gst ?? row.GST ?? row.gstTotal ?? row.GST_Total ?? 0,
    platformFee: row.platformFee ?? row.Platform_Fee ?? 0,
    deliveryFee: row.deliveryFee ?? row.Delivery_Fee ?? row.deliveryCharge ?? 0,
    convienceFee: row.convienceFee ?? row.Convience_Fee ?? 0,
    receiverName: row.receiverName ?? row.Receiver_Name ?? '',
    receiverPhone: row.receiverPhone ?? row.Receiver_Phone ?? '',
    deliveryUserId: row.deliveryUserId ?? row.Delivery_User_Id ?? null,
    deliveryUserName: row.deliveryUserName ?? row.Delivery_User_Name ?? '',
    deliveryUserPhone: row.deliveryUserPhone ?? row.Delivery_User_Phone ?? '',
    deliveryOtp: row.deliveryOtp ?? row.Delivery_OTP ?? '',
    restaurantId: row.restaurantId ?? row.Restaurant_Id ?? null,
    restaurantName: row.restaurantName ?? row.Restaurant_Name ?? '',
    deliveryAddress: row.deliveryAddress ?? row.Delivery_Address ?? null,
    deliveryAddressFormatted:
      row.deliveryAddressFormatted ?? row.Delivery_Address_Formatted ?? '',
    expectedDeliveryTime:
      row.expectedDeliveryTime ?? row.Expected_Delivery_Time ?? null,
    createdDate: row.createdDate ?? row.Created_Date ?? '',
    lineItems,
    orderLineItems: lineItems,
  };
}

export async function fetchGroceryOrdersByStore(storeId) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) return [];
  const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.groceryOrdersByStoreId(sid), {
    headers: authHeaders(),
  });
  const raw = response.data?.orders ?? (Array.isArray(response.data) ? response.data : []);
  return (Array.isArray(raw) ? raw : []).map(normalizeGroceryOrder).filter(Boolean);
}

/** GET /groceries/store/:storeId/order-history — cancelled, delivered, undelivered, completed. */
export async function fetchGroceryOrderHistoryByStore(storeId) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) return [];
  const response = await axiosGroceryInstance.get(
    GROCERY_API_PATHS.groceryOrderHistoryByStoreId(sid),
    { headers: authHeaders() }
  );
  const raw = response.data?.orders ?? (Array.isArray(response.data) ? response.data : []);
  return (Array.isArray(raw) ? raw : []).map(normalizeGroceryOrder).filter(Boolean);
}

export async function fetchGroceryOrderDetail(orderId) {
  const id = Number(orderId);
  if (!id || Number.isNaN(id)) return null;
  const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.groceryOrderById(id), {
    headers: authHeaders(),
  });
  return normalizeGroceryOrder(response.data);
}

export async function updateGroceryOrderStatus(orderId, body) {
  const id = Number(orderId);
  const response = await axiosGroceryInstance.put(
    GROCERY_API_PATHS.groceryOrderStatus(id),
    body,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

export async function createGroceryOrderPayment(orderId, body) {
  const id = Number(orderId);
  const response = await axiosGroceryInstance.post(
    GROCERY_API_PATHS.groceryOrderPayments(id),
    body,
    { headers: { 'Content-Type': 'application/json', ...authHeaders() } }
  );
  return response.data ?? {};
}

export async function fetchGroceryOrderPayments(orderId) {
  const id = Number(orderId);
  if (!id || Number.isNaN(id)) return [];
  const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.groceryOrderPayments(id), {
    headers: authHeaders(),
  });
  const raw = response.data?.payments ?? [];
  return Array.isArray(raw) ? raw : [];
}

/** Map POS payment dropdown value to store_payment_t Payment_Mode enum. */
export function mapPosPaymentTypeToMode(paymentType) {
  const key = String(paymentType || '').trim().toLowerCase();
  const map = {
    cash: 'CASH',
    card: 'CARD',
    upi: 'UPI',
    gpay: 'UPI',
    paytm: 'WALLET',
    phonepe: 'WALLET',
  };
  return map[key] || 'CASH';
}

/** Store walk-in POS order; optionally records payment when completing. */
export async function createStorePosOrder({
  storeId,
  orderStatusCode,
  createdBy,
  lineItems,
  receiverName,
  receiverPhone,
  customerId,
  orderType = 'PICKUP',
  paymentType,
  grandTotal,
}) {
  const data = await createGroceryOrder({
    storeId: Number(storeId),
    orderStatusCode,
    orderSource: 'POS',
    orderType,
    createdBy: Number(createdBy),
    ...(customerId != null ? { customerId: Number(customerId) } : {}),
    ...(receiverName ? { receiverName: String(receiverName).trim() } : {}),
    ...(receiverPhone ? { receiverPhone: String(receiverPhone).trim() } : {}),
    ...(paymentType ? { paymentType: String(paymentType).trim() } : {}),
    ...(grandTotal != null ? { amount: Number(grandTotal) } : {}),
    lineItems: (lineItems || []).map((line) => ({
      groceryItemId: line.groceryItemId,
      itemName: line.itemName,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      ...(line.unitId != null ? { unitId: line.unitId } : {}),
      ...(line.gstPercent != null ? { gstPercent: line.gstPercent } : {}),
    })),
  });

  return data;
}
